export type SignalExtractionConfig = {
  startBit: number;
  length: number;
  endianness: 'little' | 'big';
  signed: boolean;
  factor: number;
  offset: number;
};

export function validateSignalConfig(config: SignalExtractionConfig, maxPayloadBits = 64) {
  const errors: string[] = [];
  if (!Number.isInteger(config.startBit) || config.startBit < 0 || config.startBit > 63) errors.push('start_bit must be 0-63.');
  if (!Number.isInteger(config.length) || config.length < 1 || config.length > 64) errors.push('length must be 1-64.');
  if (!['little', 'big'].includes(config.endianness)) errors.push('endianness must be little or big.');
  if (!Number.isFinite(config.factor)) errors.push('factor must be a valid number.');
  if (!Number.isFinite(config.offset)) errors.push('offset must be a valid number.');
  if (config.startBit + config.length > maxPayloadBits) errors.push(`Selected range exceeds observed payload bounds (${maxPayloadBits} bits).`);
  if (config.startBit + config.length > 64) errors.push('Selected range exceeds 8-byte CAN payload bounds.');
  return errors;
}

function littleEndianRaw(bytes: number[], startBit: number, length: number) {
  let payload = 0n;
  for (let index = 0; index < Math.min(bytes.length, 8); index += 1) {
    payload |= BigInt(bytes[index]) << BigInt(index * 8);
  }
  return (payload >> BigInt(startBit)) & ((1n << BigInt(length)) - 1n);
}

function bigEndianPreviewRaw(bytes: number[], startBit: number, length: number) {
  const bitString = bytes
    .slice(0, 8)
    .map((byte) => byte.toString(2).padStart(8, '0'))
    .join('');
  const selected = bitString.slice(startBit, startBit + length);
  return selected ? BigInt(`0b${selected}`) : 0n;
}

function applySigned(raw: bigint, length: number) {
  if (length <= 0) return raw;
  const signBit = 1n << BigInt(length - 1);
  if ((raw & signBit) === 0n) return raw;
  return raw - (1n << BigInt(length));
}

export function extractSignalValue(bytes: number[], config: SignalExtractionConfig): number | null {
  if (validateSignalConfig(config, bytes.length * 8).length > 0) return null;
  if (bytes.length * 8 < config.startBit + config.length) return null;

  // Preview extraction model:
  // - little-endian treats bit 0 as the LSB of byte 0.
  // - big-endian treats bit 0 as the MSB of byte 0.
  // This is intentionally labeled as preview extraction, not full DBC/Motorola
  // export compatibility.
  const unsignedRaw = config.endianness === 'little'
    ? littleEndianRaw(bytes, config.startBit, config.length)
    : bigEndianPreviewRaw(bytes, config.startBit, config.length);
  const raw = config.signed ? applySigned(unsignedRaw, config.length) : unsignedRaw;
  return Number(raw) * config.factor + config.offset;
}
